/**
 * FilterSelect Component
 * Reusable styled select dropdown for filtering
 */

import { SelectHTMLAttributes } from 'react';

export interface FilterOption<T extends string = string> {
    value: T;
    label: string;
}

interface FilterSelectProps<T extends string = string>
    extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
    options: FilterOption<T>[];
    value: T;
    onChange: (value: T) => void;
    placeholder?: string;
    label?: string;
}

/**
 * FilterSelect - A reusable styled select for filtering
 *
 * @example
 * // Basic usage
 * <FilterSelect
 *   options={[
 *     { value: '', label: 'All Status' },
 *     { value: 'active', label: 'Active' },
 *     { value: 'inactive', label: 'Inactive' },
 *   ]}
 *   value={statusFilter}
 *   onChange={setStatusFilter}
 * />
 *
 * // With label
 * <FilterSelect
 *   label="Status"
 *   options={statusOptions}
 *   value={status}
 *   onChange={setStatus}
 * />
 */
export function FilterSelect<T extends string = string>({
    options,
    value,
    onChange,
    placeholder,
    label,
    className = '',
    id,
    ...props
}: FilterSelectProps<T>) {
    const selectId = id || (label ? `filter-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

    return (
        <div className={label ? 'flex flex-col gap-1' : ''}>
            {label && (
                <label htmlFor={selectId} className="text-sm font-medium text-gray-700">
                    {label}
                </label>
            )}
            <select
                id={selectId}
                value={value}
                onChange={(e) => onChange(e.target.value as T)}
                className={`
                    px-4 py-2 border border-gray-300 rounded-lg
                    focus:ring-2 focus:ring-primary focus:border-transparent
                    bg-white text-gray-700 cursor-pointer
                    transition-colors
                    ${className}
                `.trim().replace(/\s+/g, ' ')}
                {...props}
            >
                {placeholder && (
                    <option value="" disabled>
                        {placeholder}
                    </option>
                )}
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
}

export default FilterSelect;
